// Shared helpers used across the frontend exports module.
//
// Keep tiny + dependency-free. If you find yourself adding business logic
// here, it probably belongs in the caller.

/** CSV cell-quoting: neutralizes formula injection, then wraps in quotes only if needed. */
export function csvCell(val: unknown): string {
  if (val == null) return "";
  let s = String(val);
  // Prevent CSV/formula injection: a leading = + - @ (or tab/CR) can be executed as a formula
  // when the file is opened in Excel/Sheets. Prefix such values with a single quote so they
  // render as text — but leave plain numbers (e.g. "-5") alone so numeric columns stay numeric.
  if (/^[=+\-@\t\r]/.test(s) && !/^[+-]?(?:\d+\.?\d*|\.\d+)$/.test(s)) {
    s = `'${s}`;
  }
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Filename-safe text: alphanumeric + `._-`, max 100 chars, never empty. */
export function sanitize(name: string): string {
  return String(name).replace(/[^a-zA-Z0-9._-]/g, "_").slice(0, 100) || "_";
}

/** Trigger a browser download from in-memory text content. */
export function triggerDownload(content: string, filename: string, mime: string): void {
  triggerBlobDownload(new Blob([content], { type: mime }), filename);
}

/** Trigger a browser download from an existing Blob (e.g. zip output). */
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Split an array into fixed-size chunks. Used to bound Supabase `.in(...)`
 * lookups so portfolio-scale exports don't build an oversized request URL /
 * response, which tears down the HTTP/2/QUIC connection ("Failed to fetch").
 */
export function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/** Years between a date string and now, "X.X". Empty string if input is null. */
export function calcAge(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    const years = (Date.now() - d.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
    return years.toFixed(1);
  } catch {
    return "";
  }
}
